"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { Shield, CheckCircle2, Copy } from "lucide-react";
import QRCode from "react-qr-code";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Spinner } from "@/components/ui/spinner";
import { Logo } from "@/components/ui/logo";

import { authClient } from "@/lib/auth/client";

// ─── Schemas ─────────────────────────────────────────────────────────────────

const passwordSchema = z.object({
  password: z.string().min(1, "Password is required"),
});
type PasswordValues = z.infer<typeof passwordSchema>;

const totpSchema = z.object({
  code: z.string().length(6, "Code must be 6 digits"),
});
type TotpValues = z.infer<typeof totpSchema>;

type SetupData = {
  totpURI: string;
  backupCodes: string[];
};

type Step = "password" | "qr" | "verify" | "backup" | "done";

// ─── Component ───────────────────────────────────────────────────────────────

export function MfaSetup() {
  const router = useRouter();
  const [step, setStep] = useState<Step>("password");
  const [setupData, setSetupData] = useState<SetupData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [savedCodes, setSavedCodes] = useState(false);

  const passwordForm = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { password: "" },
  });

  const totpForm = useForm<TotpValues>({
    resolver: zodResolver(totpSchema),
    defaultValues: { code: "" },
  });

  // Step 1: Enable 2FA — get QR code URI and backup codes
  async function onEnableSubmit(values: PasswordValues) {
    setError(null);
    setLoading(true);
    try {
      const result = await authClient.twoFactor.enable({
        password: values.password,
      });
      if (result.error || !result.data) {
        const code = (result.error as { code?: string } | null)?.code;
        const message = (result.error as { message?: string } | null)?.message;
        if (code === "INVALID_PASSWORD") {
          setError("Incorrect password. Please try again.");
        } else {
          setError(message ?? `Failed to enable 2FA (${code ?? "unknown error"}). Please try again.`);
        }
        return;
      }
      const dataAny = result.data as Record<string, unknown>;
      setSetupData({
        totpURI: dataAny.totpURI as string,
        backupCodes: dataAny.backupCodes as string[],
      });
      setStep("qr");
    } catch {
      setError("Failed to enable 2FA. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // Step 2: Verify TOTP code from authenticator app
  async function onVerifySubmit(values: TotpValues) {
    setError(null);
    setLoading(true);
    try {
      const result = await authClient.twoFactor.verifyTotp({
        code: values.code,
      });
      if (result.error) {
        setError("Invalid verification code. Please try again.");
        return;
      }
      setStep("backup");
    } catch {
      setError("Invalid verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  function copyBackupCodes() {
    if (!setupData) return;
    navigator.clipboard.writeText(setupData.backupCodes.join("\n"));
    toast.success("Backup codes copied to clipboard");
  }

  // Step 3: After acknowledging backup codes, redirect to the correct dashboard.
  // Use getSession() to get the authoritative role rather than relying on stale state.
  async function handleDone() {
    if (!savedCodes) {
      toast.error("Please confirm you've saved your backup codes.");
      return;
    }
    setLoading(true);
    setStep("done");
    toast.success("Two-factor authentication enabled!");
    try {
      const sessionResult = await authClient.getSession();
      const role = (sessionResult.data?.user as Record<string, unknown> | undefined)
        ?.role as string | undefined;
      if (role === "admin") router.push("/admin/dashboard");
      else if (role === "investor") router.push("/investor/dashboard");
      else router.push("/founder/dashboard");
    } catch {
      // Fallback: if session fetch fails, go to root and let middleware handle it
      router.push("/");
    } finally {
      setLoading(false);
    }
  }

  // ─── Done state ───────────────────────────────────────────────────────────
  if (step === "done") {
    return (
      <div className="w-full max-w-md mx-auto text-center space-y-4">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-xl font-display font-bold text-foreground">
          2FA Enabled Successfully
        </h2>
        <p className="text-sm text-muted-foreground">
          Your account is now protected with two-factor authentication.
        </p>
        {loading && (
          <div className="flex justify-center">
            <Spinner className="w-5 h-5" />
          </div>
        )}
      </div>
    );
  }

  // ─── Backup codes ─────────────────────────────────────────────────────────
  if (step === "backup" && setupData) {
    return (
      <div className="w-full max-w-md mx-auto space-y-6">
        <div className="text-center">
          <h2 className="text-xl font-display font-bold text-foreground">
            Save Your Recovery Codes
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Store these codes safely. Each can only be used once.
          </p>
        </div>

        <div className="bg-muted rounded-lg p-4">
          <div className="grid grid-cols-2 gap-2">
            {setupData.backupCodes.map((code, i) => (
              <code
                key={i}
                className="font-mono text-sm bg-card border border-border rounded px-2 py-1 text-center"
              >
                {code}
              </code>
            ))}
          </div>
        </div>

        <Button variant="outline" className="w-full gap-2" onClick={copyBackupCodes}>
          <Copy className="w-4 h-4" />
          Copy All Codes
        </Button>

        <div className="flex items-start space-x-3">
          <Checkbox
            id="saved-codes"
            checked={savedCodes}
            onCheckedChange={(checked) => setSavedCodes(checked === true)}
          />
          <label
            htmlFor="saved-codes"
            className="text-sm text-muted-foreground cursor-pointer leading-relaxed"
          >
            I&apos;ve saved my recovery codes in a safe place. I understand that if I lose access
            to my authenticator app and these codes, I won&apos;t be able to log in.
          </label>
        </div>

        <Button
          className="w-full btn-premium text-white"
          onClick={handleDone}
          disabled={!savedCodes || loading}
        >
          {loading ? (
            <>
              <Spinner className="mr-2" />
              Redirecting...
            </>
          ) : (
            "Complete Setup"
          )}
        </Button>
      </div>
    );
  }

  // ─── QR code + TOTP verification ──────────────────────────────────────────
  if (step === "verify" || step === "qr") {
    // Parse the base32 secret from the TOTP URI so users can enter it manually.
    // The full URI (otpauth://totp/...) is only needed by the QR scanner.
    const manualSecret = (() => {
      try {
        return (
          new URL(setupData?.totpURI ?? "").searchParams.get("secret") ??
          setupData?.totpURI ??
          ""
        );
      } catch {
        return setupData?.totpURI ?? "";
      }
    })();

    return (
      <div className="w-full max-w-md mx-auto space-y-6">
        <div className="text-center">
          <h2 className="text-xl font-display font-bold text-foreground">Scan QR Code</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Open your authenticator app (Google Authenticator, Authy, etc.) and scan the code
            below.
          </p>
        </div>

        {setupData && (
          <div className="flex flex-col items-center gap-4">
            {/* Client-side SVG QR code — no external service, no TOTP URI leakage */}
            <div className="bg-white p-4 rounded-lg border border-border flex items-center justify-center">
              <QRCode value={setupData.totpURI} size={200} />
            </div>

            <div className="w-full">
              <p className="text-xs text-muted-foreground text-center mb-2">
                Can&apos;t scan? Enter this key manually in your authenticator app:
              </p>
              {/* Display only the base32 secret, not the full otpauth:// URI */}
              <div className="bg-muted rounded px-3 py-2 text-xs font-mono break-all text-center select-all">
                {manualSecret}
              </div>
            </div>
          </div>
        )}

        <Separator />

        <div className="space-y-4">
          <h3 className="text-sm font-medium text-foreground text-center">
            Enter the 6-digit code from your app
          </h3>
          <Form {...totpForm}>
            <form onSubmit={totpForm.handleSubmit(onVerifySubmit)} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <FormField
                control={totpForm.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="flex justify-center">
                        <InputOTP
                          maxLength={6}
                          value={field.value}
                          onChange={field.onChange}
                        >
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                    </FormControl>
                    <FormMessage className="text-center" />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                className="w-full btn-premium text-white"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner className="mr-2" />
                    Verifying...
                  </>
                ) : (
                  "Verify Code"
                )}
              </Button>
            </form>
          </Form>
        </div>
      </div>
    );
  }

  // ─── Step 1: Password confirmation ────────────────────────────────────────
  return (
    <div className="w-full max-w-md mx-auto space-y-6">
      <div className="flex flex-col items-center gap-4">
        <Logo imageClassName="h-10 w-auto" />
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
            <Shield className="w-6 h-6 text-primary" />
          </div>
          <h1 className="text-2xl font-display font-bold text-foreground">
            Set Up Two-Factor Authentication
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Protect your account with an authenticator app.
          </p>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
        <Form {...passwordForm}>
          <form onSubmit={passwordForm.handleSubmit(onEnableSubmit)} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <FormField
              control={passwordForm.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm your password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      autoComplete="current-password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="w-full btn-premium text-white"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner className="mr-2" />
                  Setting up...
                </>
              ) : (
                "Continue"
              )}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
}
